import { Box, LinearProgress } from "@mui/material";
import {
  GridFilterModel,
  GridColumnVisibilityModel,
  GridColDef,
  GridColumnGroup,
} from "@mui/x-data-grid-premium";
import React from "react";

import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { useIsMobile } from "hooks/useIsMobile";
import {
  ChildMixUpdate,
  MixGrouping,
  MixMaterial,
  SingleMixSummary,
  UnitType,
} from "src/store/api/generatedApi";

import { useTenantData } from "contexts/tenant";
import { usePercentSerialiser } from "hooks/serialisers/percent";
import { useFormatNumber } from "src/utils/formatNumber";
import { MixCollapse } from "hooks/useMixCollapse";
import {
  basketWeight,
  LayerEditingHooks,
  collapseHostField,
  copperGroup,
  driWeight,
  hideCollapsedValues,
  material as materialColumn,
  mixSummary,
  nameOverride,
  numHeats,
  PopoverLayerEditor,
  popoverSteelGrades,
  simpleSteelGrades,
  subStandardGroup,
  withCollapseToggle,
} from "./columns";
import { Detail } from "./MixSummaryCell";
import { useSx } from "./sx";
import { Table } from "./Table";
import { Material, Row } from "./types";

const EMPTY_MIX_IDS = new Set<number>();
export const shouldShowNameOverride = (
  options: string[] | undefined,
  requiredMixIds: Set<number> | undefined
) => (options?.length ?? 0) > 0 || (requiredMixIds?.size ?? 0) > 0;

export interface EditableMixTableProps {
  columnGroupingModel: GridColumnGroup[];
  filterModel: GridFilterModel;
  columnVisibilityModel?: GridColumnVisibilityModel;
  columnOverrides?: GridColDef<Row>[];
  loading?: boolean;
  disableEdit: boolean;
  hasEdited: boolean;
  rows: Row[];
  materials: Material[];
  selectedSteelGradeIds: number[];
  onChange: (rows: Row[]) => void;
  mixGrouping: MixGrouping;
  hasDri: boolean;
  steelGradesWidth: number;
  onSubmit?: (updates: ChildMixUpdate[], comment: string) => Promise<void>;
  setMixDescription?: (mixId: number, description: string) => void;
  massScaleFactorOverride?: number;
  isPercentageModeOverride?: boolean;
  showSummary?: boolean;
  preloadedSummaries?: Record<number, SingleMixSummary | null | undefined>;
  preloadedDris?: Record<number, MixMaterial | null | undefined>;
  popoverLayerEditor?: PopoverLayerEditor;
  layerEditing?: LayerEditingHooks;
  nameOverrideOptions?: string[];
  mixIdsRequiringNameOverride?: Set<number>;
  collapse?: MixCollapse;
}

export const useColumns = (
  materials: Material[],
  selectedSteelGradeIds: number[],
  massUnit: UnitType,
  massScaleFactor: number,
  disableEdit: boolean,
  hasEdited: boolean,
  mixGrouping: MixGrouping,
  hasDri: boolean,
  columnGroupingModel: GridColumnGroup[],
  steelGradesWidth: number,
  isPercentageMode: boolean,
  displayUnitType: UnitType,
  onSubmit?: (updates: ChildMixUpdate[], comment: string) => Promise<void>,
  setMixDescription?: (mixId: number, description: string) => void,
  showSummary = true,
  preloadedSummaries?: Record<number, SingleMixSummary | null | undefined>,
  preloadedDris?: Record<number, MixMaterial | null | undefined>,
  popoverLayerEditor?: PopoverLayerEditor,
  nameOverrideOptions?: string[],
  mixIdsRequiringNameOverride = EMPTY_MIX_IDS,
  loading = false,
  layerEditing?: LayerEditingHooks
) => {
  const bracketUnits = useUnitsFormatter(true);
  const units = useUnitsFormatter(false);
  const { t } = useTenantTranslation();
  const { rounding_units } = useTenantData();
  const materialFormat = useFormatNumber()(3, false);
  const totalFormat = useFormatNumber()(0, true);
  const showNameOverride = shouldShowNameOverride(
    nameOverrideOptions,
    mixIdsRequiringNameOverride
  );
  const { format: percentageFormatter } = usePercentSerialiser({
    decimalPlaces: 1,
    includeSymbol: true,
  });
  const groupingColumn = React.useMemo(() => {
    switch (mixGrouping) {
      case "copper_group":
        return copperGroup;
      case "grouping_name":
        return subStandardGroup(t);
    }
  }, [mixGrouping, t]);
  const summaryDetails: Detail[] = React.useMemo(() => {
    if (!showSummary) return [];
    switch (mixGrouping) {
      case "copper_group":
        return ["price_diff", "cu"];
      case "grouping_name":
        return ["price_diff"];
    }
  }, [mixGrouping, showSummary]);

  const columns = React.useMemo(() => {
    return [
      mixSummary(units, summaryDetails, preloadedSummaries),
      numHeats(onSubmit, disableEdit, hasEdited),
      showSummary
        ? popoverSteelGrades(
            t,
            steelGradesWidth,
            selectedSteelGradeIds,
            materials,
            hasDri,
            columnGroupingModel,
            disableEdit,
            setMixDescription,
            preloadedSummaries,
            preloadedDris,
            popoverLayerEditor
          )
        : simpleSteelGrades(t, steelGradesWidth, selectedSteelGradeIds),
      groupingColumn,
      ...(showNameOverride
        ? [
            nameOverride(
              t,
              nameOverrideOptions ?? [],
              disableEdit,
              mixIdsRequiringNameOverride
            ),
          ]
        : []),
      ...(hasDri
        ? [
            driWeight(
              t,
              massUnit,
              massScaleFactor,
              bracketUnits,
              preloadedDris,
              loading
            ),
          ]
        : []),
      basketWeight(
        t,
        massUnit,
        massScaleFactor,
        totalFormat,
        bracketUnits,
        disableEdit,
        displayUnitType,
        layerEditing
      ),
      ...materials.map((material): GridColDef<Row> =>
        materialColumn(
          material,
          massScaleFactor,
          disableEdit,
          materialFormat,
          rounding_units,
          isPercentageMode ? percentageFormatter : undefined
        )
      ),
    ];
  }, [
    materials,
    selectedSteelGradeIds,
    disableEdit,
    summaryDetails,
    groupingColumn,
    hasDri,
    massUnit,
    massScaleFactor,
    bracketUnits,
    totalFormat,
    steelGradesWidth,
    columnGroupingModel,
    isPercentageMode,
    displayUnitType,
    t,
    units,
    onSubmit,
    hasEdited,
    setMixDescription,
    showSummary,
    preloadedSummaries,
    preloadedDris,
    materialFormat,
    percentageFormatter,
    rounding_units,
    popoverLayerEditor,
    nameOverrideOptions,
    mixIdsRequiringNameOverride,
    showNameOverride,
    loading,
    layerEditing,
  ]);

  return columns;
};

export const EditableMixTable = ({
  disableEdit,
  hasEdited,
  materials,
  selectedSteelGradeIds,
  rows,
  onChange,
  columnGroupingModel,
  filterModel: steelGradeFilterModel,
  columnVisibilityModel = {},
  columnOverrides = [],
  loading = false,
  mixGrouping,
  hasDri,
  steelGradesWidth,
  onSubmit,
  setMixDescription,
  massScaleFactorOverride,
  isPercentageModeOverride,
  showSummary = true,
  preloadedSummaries,
  preloadedDris,
  popoverLayerEditor,
  layerEditing,
  nameOverrideOptions,
  mixIdsRequiringNameOverride,
  collapse: collapseProp,
}: EditableMixTableProps) => {
  const { t } = useTenantTranslation();
  const hasNameOverride = shouldShowNameOverride(
    nameOverrideOptions,
    mixIdsRequiringNameOverride
  );
  const isMobile = useIsMobile();
  const { default_mix_breakdown, small_mass_per_mass } = useTenantData();
  const massScaleFactor =
    massScaleFactorOverride ??
    (default_mix_breakdown === "small_mass" ? small_mass_per_mass : 1);
  const isPercentageMode = isPercentageModeOverride ?? false;
  const massUnit =
    default_mix_breakdown === "small_mass" ? "small_mass" : "mass";
  const displayUnitType: UnitType = isPercentageMode
    ? "mass_fraction"
    : massUnit;
  const hasMultipleMixes = new Set(rows.map((row) => row.mixId)).size > 1;
  const collapse = collapseProp && hasMultipleMixes ? collapseProp : undefined;

  const columns = useColumns(
    materials,
    selectedSteelGradeIds,
    massUnit,
    massScaleFactor,
    disableEdit,
    hasEdited,
    mixGrouping,
    hasDri,
    columnGroupingModel,
    steelGradesWidth,
    isPercentageMode,
    displayUnitType,
    onSubmit,
    setMixDescription,
    showSummary,
    preloadedSummaries,
    preloadedDris,
    popoverLayerEditor,
    nameOverrideOptions,
    mixIdsRequiringNameOverride,
    loading,
    layerEditing
  );

  const configuredColumns = React.useMemo(
    () => [
      ...columns.map(
        (column) =>
          columnOverrides.find((override) => override.field === column.field) ??
          column
      ),
      ...columnOverrides.filter(
        (override) => !columns.some((column) => column.field === override.field)
      ),
    ],
    [columns, columnOverrides]
  );
  const columnsWithCollapse = React.useMemo(
    () =>
      collapse
        ? configuredColumns.map((column) => {
            const blanked = hideCollapsedValues(
              column,
              collapse.collapsedMixIds
            );
            return column.field === collapseHostField
              ? withCollapseToggle(
                  blanked,
                  t,
                  collapse.collapsedMixIds,
                  collapse.onToggleCollapse
                )
              : blanked;
          })
        : configuredColumns,
    [collapse, configuredColumns, t]
  );

  const pinnedColumns = React.useMemo(() => {
    if (isMobile) {
      return columnsWithCollapse.slice(0, 2).map((column) => column.field);
    }
    const pinnedCount = (hasDri ? 6 : 5) + (hasNameOverride ? 1 : 0);
    return columnsWithCollapse
      .slice(0, pinnedCount)
      .map((column) => column.field);
  }, [columnsWithCollapse, isMobile, hasDri, hasNameOverride]);

  const filterModel = React.useMemo(
    () =>
      collapse && collapse.collapsedMixIdArray.length > 0
        ? {
            ...steelGradeFilterModel,
            items: [
              ...steelGradeFilterModel.items,
              {
                id: "collapse",
                field: collapseHostField,
                operator: "notCollapsed",
                value: collapse.collapsedMixIdArray,
              },
            ],
          }
        : steelGradeFilterModel,
    [steelGradeFilterModel, collapse]
  );
  const sx = useSx();
  return (
    <Box
      aria-busy={loading}
      sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}
    >
      <Box sx={{ height: 4 }}>
        {loading ? <LinearProgress aria-label={t("loading")} /> : null}
      </Box>
      <Table
        filterModel={filterModel}
        onChange={onChange}
        rows={rows}
        columnGroupingModel={preloadedSummaries ? [] : columnGroupingModel}
        columns={columnsWithCollapse}
        disableEdit={disableEdit}
        pinnedColumns={pinnedColumns}
        sx={sx}
        columnVisibilityModel={columnVisibilityModel}
        collapsedMixIds={collapse?.collapsedMixIds}
      />
    </Box>
  );
};
